/**
 * 🧪 useSimulationRibbon — Hook de configuração do Ribbon para Simulação
 *
 * Retorna RibbonConfig com grupos: Cenário, Execução, Análise, Ferramentas
 */

import { useMemo } from 'react';
import {
  MessageSquarePlus, Save, Copy, Trash2,
  PlayCircle, StopCircle,
  BarChart3, TrendingUp, RefreshCw, Download,
  FileText, FileSpreadsheet, Settings, List, SplitSquareHorizontal,
} from 'lucide-react';
import type { RibbonConfig } from '../types/ribbon';

const NOOP = () => {};

export type SimViewMode = 'table' | 'detail';

export interface SimulationRibbonDeps {
  // Cenário
  onNewScenario?: () => void;
  onSave?: () => void;
  onDuplicate?: () => void;
  onDelete?: () => void;
  hasSelection?: boolean;
  // Execução
  onRunSimulation?: () => void;
  onStopSimulation?: () => void;
  isRunning?: boolean;
  // Exportar
  onExportPDF?: () => void;
  onExportExcel?: () => void;
  // Análise
  onShowComparison?: () => void;
  onShowDashboard?: () => void;
  onRefresh?: () => void;
  // Ferramentas
  onSettings?: () => void;
  // View mode
  viewMode?: SimViewMode;
  onViewModeChange?: (mode: SimViewMode) => void;
  // Filters slot
  filtersWidget?: React.ReactNode;
}

export function useSimulationRibbon(deps: SimulationRibbonDeps): RibbonConfig {
  return useMemo<RibbonConfig>(() => ({
    moduleId: 'simulation',
    groups: [
      // ─── Cenário (LEFT) ──────────────────────────
      {
        id: 'cenario',
        label: 'Cenário',
        position: 'left',
        actions: [
          { id: 'new-scenario', label: 'Simulação', icon: MessageSquarePlus, onClick: deps.onNewScenario ?? NOOP },
          { id: 'save', label: 'Salvar', icon: Save, onClick: deps.onSave ?? NOOP, variant: 'primary', disabled: !deps.onSave },
          { id: 'duplicate', label: 'Duplicar', icon: Copy, onClick: deps.onDuplicate ?? NOOP, disabled: !deps.hasSelection },
          { id: 'delete', label: 'Excluir', icon: Trash2, onClick: deps.onDelete ?? NOOP, disabled: !deps.hasSelection, variant: 'danger' },
        ],
      },
      // ─── Execução (CENTER) ───────────────────────
      {
        id: 'execucao',
        label: 'Execução',
        position: 'center',
        actions: [
          { id: 'run', label: 'Executar', icon: PlayCircle, onClick: deps.onRunSimulation ?? NOOP, variant: 'primary', disabled: !deps.hasSelection || deps.isRunning },
          { id: 'stop', label: 'Parar', icon: StopCircle, onClick: deps.onStopSimulation ?? NOOP, variant: 'danger', disabled: !deps.isRunning },
        ],
      },
      // ─── Exportar (CENTER) ───────────────────────
      {
        id: 'exportar',
        label: 'Exportar',
        position: 'center',
        actions: [
          {
            id: 'export-menu',
            label: 'Exportar',
            icon: Download,
            onClick: NOOP,
            dropdown: [
              { id: 'export-excel', label: 'Exportar Excel', icon: FileSpreadsheet, onClick: deps.onExportExcel ?? NOOP },
              { id: 'export-pdf', label: 'Exportar PDF', icon: FileText, onClick: deps.onExportPDF ?? NOOP },
            ],
          },
        ],
      },
      // ─── Visualização (CENTER) ───────────────────
      {
        id: 'visualizacao',
        label: 'Visualização',
        position: 'center',
        actions: [
          { id: 'view-table', label: 'Tabela', icon: List, onClick: () => deps.onViewModeChange?.('table'), active: deps.viewMode === 'table' },
          { id: 'view-detail', label: 'Detalhe', icon: SplitSquareHorizontal, onClick: () => deps.onViewModeChange?.('detail'), active: deps.viewMode === 'detail' },
        ],
      },
      // ─── Análise (RIGHT) ─────────────────────────
      {
        id: 'analise',
        label: 'Análise',
        position: 'right',
        actions: [
          { id: 'compare', label: 'Comparar', icon: TrendingUp, onClick: deps.onShowComparison ?? NOOP, tooltip: 'Comparar cenários lado a lado' },
          { id: 'dashboard', label: 'Dashboard', icon: BarChart3, onClick: deps.onShowDashboard ?? NOOP },
          { id: 'refresh', label: 'Atualizar', icon: RefreshCw, onClick: deps.onRefresh ?? NOOP },
        ],
      },
      // ─── Ferramentas (RIGHT) ─────────────────────
      {
        id: 'ferramentas',
        label: 'Ferramentas',
        position: 'right',
        actions: [
          { id: 'settings', label: 'Configurar', icon: Settings, onClick: deps.onSettings ?? NOOP },
        ],
      },
    ],
    slots: {
      afterGroups: deps.filtersWidget,
    },
  }), [deps]);
}
